import { useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ConfirmDelete, DetailRow, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ConfirmAction } from '@/components/confirm';
import { BackLink, recordGate } from '@/components/record';
import ContactDialog from '@/components/admin/ContactDialog';
import { CONTACT_STATUS_STYLES } from '@/shared/constants';
import { CONTACT_FIELDS } from '@/shared/fields';
import { committed } from '@/shared/data';
import { formatLocalDate, DATE_FORMATS } from '@/utils/date';

export const route = { path: '/admin/messages/:id', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };

export default function AdminMessageDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [editOpen, setEditOpen] = useState(false);

  const { data: message, loading, error, run } = useFetch(
    () => sdk.table('contact_messages_c').select(CONTACT_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const back = <BackLink to="/admin/messages" label="Back to Messages" />;
  const gate = recordGate({ back, loading, record: message, error, run, what: 'Message' });
  if (gate) return gate;

  const mailto = `mailto:${message.email_c}?subject=${encodeURIComponent('Re: your message to Ghost Cable')}&body=${encodeURIComponent(
    `Hi ${message.Name.split(' ')[0]},\n\n\n\n> ${message.message_c.split('\n').join('\n> ')}`,
  )}`;

  async function setStatus(status, text) {
    const res = await sdk.table('contact_messages_c').update({ Id: message.Id, status_c: status });
    if (!committed(res, text)) return false;
    run();
    return true;
  }

  async function remove() {
    const res = await sdk.table('contact_messages_c').remove(message.Id);
    if (!committed(res, 'Message deleted')) return false;
    navigate('/admin/messages');
    return true;
  }

  return (
    <div className="mx-auto max-w-3xl">
      {back}
      <PageHeader
        eyebrow={`Message · ${formatLocalDate(message.CreatedOn, DATE_FORMATS.WITH_TIME)}`}
        title={message.Name}
        description={message.email_c}
        actions={
          <>
            <StatusBadge value={message.status_c} styles={CONTACT_STATUS_STYLES} />
            <Button variant="outline" size="sm" onClick={() => setEditOpen(true)}>
              <ApperIcon name="Pencil" size={16} />
              Edit
            </Button>
            <ConfirmDelete title="Delete this message?" description="This cannot be undone." onConfirm={remove} />
          </>
        }
      />

      <Panel title="Message" icon="Mail" bodyClassName="py-4">
        <p className="whitespace-pre-wrap text-sm leading-relaxed">{message.message_c}</p>
      </Panel>

      <div className="my-4 flex flex-wrap gap-2">
        <Button size="sm" asChild>
          <a href={mailto}>
            <ApperIcon name="Reply" size={16} />
            Reply by email
          </a>
        </Button>
        {message.status_c !== 'Replied' && (
          <ConfirmAction
            label="Mark replied"
            icon="CircleCheck"
            title="Mark as replied?"
            description="Use this once your answer has gone out."
            busyLabel="Saving…"
            onConfirm={() => setStatus('Replied', 'Marked as replied')}
          />
        )}
        {message.status_c !== 'Archived' ? (
          <ConfirmAction
            label="Archive"
            icon="Archive"
            title="Archive this message?"
            description="It leaves the New queue. You can bring it back later."
            busyLabel="Archiving…"
            onConfirm={() => setStatus('Archived', 'Message archived')}
          />
        ) : (
          <ConfirmAction
            label="Move to New"
            icon="Inbox"
            title="Move back to New?"
            description="It shows up in the New queue again."
            busyLabel="Moving…"
            onConfirm={() => setStatus('New', 'Moved to New')}
          />
        )}
      </div>

      <Panel title="Details" icon="Info">
        <dl className="divide-y divide-border">
          <DetailRow label="From">{message.Name}</DetailRow>
          <DetailRow label="Email">{message.email_c}</DetailRow>
          <DetailRow label="Status">
            <StatusBadge value={message.status_c} styles={CONTACT_STATUS_STYLES} />
          </DetailRow>
          <DetailRow label="Received">{formatLocalDate(message.CreatedOn, DATE_FORMATS.WITH_TIME)}</DetailRow>
        </dl>
      </Panel>

      <ContactDialog open={editOpen} onOpenChange={setEditOpen} message={message} onSaved={run} />
    </div>
  );
}
